import React, { useEffect, useState } from 'react';
import { AdvancedMarker, useMap } from '@vis.gl/react-google-maps';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';

const MyLocationMarker = ({ mapCenter, defaultCenter, onLocationUpdate }) => {
  const [currentLocation, setCurrentLocation] = useState(null);
  const [initialLocation, setInitialLocation] = useState(null);
  const [locationAcquired, setLocationAcquired] = useState(false);
  const [geoPrompt, setGeoPrompt] = useState(false);
  const [geoDenied, setGeoDenied] = useState(false);
  const [watching, setWatching] = useState(false);

  // Get the map instance using the hook
  const map = useMap();

  // Robust location acquisition with retry logic
  useEffect(() => {
    if (locationAcquired) return;
    
    let didCancel = false;
    let retryTimeoutId;

    function tryGetLocation() {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          if (didCancel) return;
          const { latitude, longitude } = position.coords;
          const location = { lat: latitude, lng: longitude };
          console.log('[MyLocationMarker] Initial location acquired:', location);
          setCurrentLocation(location);
          setInitialLocation(location);
          setLocationAcquired(true);
          
          // Notify parent component of location update
          if (onLocationUpdate) {
            onLocationUpdate(location);
          }
          
          // Programmatically center the map if it's loaded
          if (map) {
            console.log('[MyLocationMarker] Centering map on user location:', location);
            map.panTo(location);
            map.setZoom(20);
          } else {
            console.log('[MyLocationMarker] Map not loaded yet, will center when loaded');
          }
        },
        (error) => {
          console.log('[MyLocationMarker] Geolocation error:', error);
          if (!didCancel) {
            if (error.code === 1) {
              // Permission denied
              setGeoDenied(true);
            } else if (error.code === 2) {
              // Position unavailable
              retryTimeoutId = setTimeout(tryGetLocation, 1000);
            } else {
              // Timeout or other error
              retryTimeoutId = setTimeout(tryGetLocation, 1000);
            }
          }
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000,
        }
      );
    }

    tryGetLocation();

    return () => {
      didCancel = true;
      if (retryTimeoutId) {
        clearTimeout(retryTimeoutId);
      }
    };
  }, [locationAcquired, map, onLocationUpdate]);

  // Start polling for location updates as soon as initialLocation is set
  useEffect(() => {
    if (
      initialLocation &&
      mapCenter &&
      defaultCenter &&
      (mapCenter.lat !== defaultCenter.lat || mapCenter.lng !== defaultCenter.lng) &&
      !watching
    ) {
      console.log('[MyLocationMarker] Starting to watch location');
      setWatching(true);
      const watchId = navigator.geolocation.watchPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          console.log('[MyLocationMarker] Polled location:', latitude, longitude);
          const newLocation = { lat: latitude, lng: longitude };
          setCurrentLocation(newLocation);
          
          // Notify parent component of location update
          if (onLocationUpdate) {
            onLocationUpdate(newLocation);
          }
        },
        (error) => {
          console.log('[MyLocationMarker] Watch position error:', error);
          // No error UI, just keep retrying
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 1000,
        }
      );
      return () => {
        navigator.geolocation.clearWatch(watchId);
      };
    }
  }, [initialLocation, mapCenter, defaultCenter, watching, onLocationUpdate]);

  const handleMyLocationClick = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          const location = { lat: latitude, lng: longitude };
          console.log('[MyLocationMarker] My Location button clicked, centering on:', location);
          setCurrentLocation(location);
          
          // Notify parent component of location update
          if (onLocationUpdate) {
            onLocationUpdate(location);
          }
          
          // Use the map instance to pan to location
          if (map) {
            console.log('[MyLocationMarker] Map instance exists, attempting to pan');
            try {
              map.panTo(location);
              map.setZoom(20);
              console.log('[MyLocationMarker] Successfully panned map to location');
            } catch (error) {
              console.error('[MyLocationMarker] Error panning map:', error);
            }
          } else {
            console.log('[MyLocationMarker] Map instance is null, cannot pan');
          }
        },
        (error) => {
          console.log('[MyLocationMarker] Geolocation error:', error);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000,
        }
      );
    }
  };

  const handleLocationPrompt = () => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        // After user responds to prompt, update state and acquire location
        setGeoPrompt(false);
        setGeoDenied(false);
        const { latitude, longitude } = position.coords;
        const location = { lat: latitude, lng: longitude };
        setCurrentLocation(location);
        setInitialLocation(location);
        setLocationAcquired(true);
        
        // Notify parent component of location update
        if (onLocationUpdate) {
          onLocationUpdate(location);
        }
        
        if (map) {
          map.panTo(location);
          map.setZoom(20);
        }
      },
      () => {
        // If denied or error, re-check permission state
        navigator.permissions.query({ name: 'geolocation' }).then(function(result) {
          if (result.state === 'denied') {
            setGeoPrompt(false);
            setGeoDenied(true);
          }
        });
      }
    );
  };

  // Location permission prompt
  if (geoPrompt) {
    return (
      <div style={{
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#181818',
        color: '#fff',
        borderRadius: 12,
        minHeight: 300
      }}>
        <p style={{ fontSize: 18, marginBottom: 16, textAlign: 'center' }}>
          Please allow location access to use this feature.
        </p>
        <button
          style={{
            background: '#222',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            padding: '12px 28px',
            fontWeight: 700,
            fontSize: 16,
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            marginTop: 8
          }}
          onClick={handleLocationPrompt}
        >
          Allow Location Access
        </button>
      </div>
    );
  }

  // Location denied state
  if (geoDenied) {
    return (
      <div style={{
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#181818',
        color: '#fff',
        borderRadius: 12,
        minHeight: 300
      }}>
        <p style={{ fontSize: 18, marginBottom: 16, textAlign: 'center' }}>
          Location access denied. Please enable location services in your browser settings.
        </p>
        <button
          style={{
            background: '#222',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            padding: '12px 28px',
            fontWeight: 700,
            fontSize: 16,
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            marginTop: 8
          }}
          onClick={() => {
            alert('To enable location, go to your browser settings and allow location access for this site.');
          }}
        >
          How to Enable Location
        </button>
      </div>
    );
  }

  return (
    <>
      {/* My Location Marker */}
      {currentLocation && (
        <AdvancedMarker
          position={currentLocation}
          title="My Location"
        >
          <div style={{
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            backgroundColor: '#4285F4',
            border: '2px solid #ffffff',
            boxShadow: '0 2px 6px rgba(0,0,0,0.3)'
          }} />
        </AdvancedMarker>
      )}
      
      {/* My Location Button */}
      
      <IconButton 
        id="my-location-btn" 
        onClick={handleMyLocationClick} 
        aria-label="My Location" 
        size="large"
      >
        <MyLocationIcon />
      </IconButton>
      
    </>
  );
};

export default MyLocationMarker;